import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import tankCleaningService from '../../services/TankCleaningService.js';
import TankCleaningFileCabinet from './TankCleaningFileCabinet.js';
import { ClipboardCheck, Pencil, ArrowLeft, Download, CheckCircle, XCircle, FolderOpen, DollarSign } from "lucide-react";
import { getViewPageActionButtons, isViewPageActionDisabled } from '../../utils/roleBasedUIHelpers.js';
import { ROLES } from '../../utils/permissions.js';

const ViewTankCleaning = () => {
  const { cleaningId } = useParams();
  const [cleaning, setCleaning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFileCabinet, setShowFileCabinet] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const userRole = user?.role_name || user?.role;
  const isClient = userRole === ROLES.CLIENT;
  const isAccountant = userRole === ROLES.ACCOUNTANT;
  const isViewOnlyRole = isClient || isAccountant;
  
  // Get action buttons based on role
  const actionButtons = getViewPageActionButtons(userRole);
  const isActionDisabled = (action) => isViewPageActionDisabled(userRole, action);

  useEffect(() => {
    fetchCleaning();
  }, [cleaningId]);

  const fetchCleaning = async () => {
    try {
      const response = await tankCleaningService.getById(cleaningId);
      setCleaning(response.data);
    } catch (err) {
      Alert.error('Failed to load cleaning details');
      console.error(err);
      navigate('/tank-cleanings');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCertificate = async () => {
    try {
      const blob = await tankCleaningService.generateCertificate(cleaningId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tank_cleaning_${(cleaning.tank_no || cleaning.cleaning_id).replace(/\//g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success('Certificate downloaded successfully!');
    } catch (err) {
      Alert.error('Failed to download certificate');
    }
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      'paid': {
        bg: 'bg-green-100',
        text: 'text-green-700',
        label: 'Paid',
        icon: CheckCircle
      },
      'unpaid': {
        bg: 'bg-red-100',
        text: 'text-red-700',
        label: 'Unpaid',
        icon: XCircle
      },
      'pending': {
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
        label: 'Payment Pending',
        icon: DollarSign
      }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    const Icon = config.icon;

    return (
      <span className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const renderCheckbox = (checked) => {
    return checked ? (
      <span className="text-green-600 font-semibold flex items-center gap-1">
        <CheckCircle className="w-4 h-4" /> YES
      </span>
    ) : (
      <span className="text-red-600 font-semibold flex items-center gap-1">
        <XCircle className="w-4 h-4" /> NO
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-sm sm:text-base text-gray-600">Loading cleaning details...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!cleaning) {
    return null;
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-8xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <ClipboardCheck className="w-5 h-5 text-buttonBg" />
                      Tank Cleaning Details
                    </h1>
                  </div>
                  <button
                    onClick={() => navigate('/tank-cleanings')}
                    className="relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-buttonBg text-buttonTxt font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:bg-transparent hover:text-buttonBg">
                    {/* COLLAPSING BACKGROUND */}
                    <span className="absolute inset-0 bg-buttonBg scale-x-100 group-hover:scale-x-0 origin-center transition-transform duration-300 ease-out z-0"/>
                      {/* ICON */}
                      <ArrowLeft className="w-4 h-4 relative z-10 transition-transform duration-300 ease-out group-hover:scale-90"/>
                        <span className="relative z-10">Back to Tank Cleaning</span>
                  </button>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-sm shadow-sm overflow-hidden">
                  {/* Profile Header Section */}
                  <div className="bg-primary p-3 px-4">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 lg:gap-4">
                      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-3">
                        <h2 className="text-xl sm:text-lg md:text-lg font-semibold text-secondary mb-0 break-words">
                          {cleaning.cleaning_id}
                        </h2>
                        {!isClient && (
                          <div className="flex flex-wrap items-center gap-2">
                            {cleaning.send_to_dm && (
                              <span className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                <CheckCircle className="w-3 h-3" />
                                Sent to Documentation Manager
                              </span>
                            )}
                            {cleaning.published && (
                              <span className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3" />
                                Published to Client
                              </span>
                            )}
                            {!cleaning.send_to_dm && !cleaning.published && (
                              <span className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                <XCircle className="w-3 h-3" />
                                Draft
                              </span>
                            )}
                            {cleaning.payment_status && getPaymentStatusBadge(cleaning.payment_status)}
                          </div>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="w-full lg:w-auto flex flex-col sm:flex-row gap-2 sm:gap-3">
                        {actionButtons.view && (
                          <button onClick={() => setShowFileCabinet(true)}
                            disabled={isActionDisabled('view')}
                            className={`relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-white text-buttonBg font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:text-white ${isActionDisabled('view') ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                            <span className="absolute inset-0 bg-buttonBg scale-x-0 group-hover:scale-x-100 origin-center transition-transform duration-300 ease-out z-0"/>
                            <FolderOpen className="w-4 h-4 relative z-10 transition-transform duration-300 ease-out group-hover:scale-90" />
                            <span className="relative z-10">File Cabinet</span>
                          </button>
                        )}

                        {actionButtons.downloadPDF && (
                          <button
                            onClick={handleDownloadCertificate}
                            disabled={isActionDisabled('downloadPDF') || (isClient && !cleaning.published)}
                            title={isClient && !cleaning.published ? 'PDF download is only available after publishing' : ''}
                            className={`relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-white text-buttonBg font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:text-white ${(isActionDisabled('downloadPDF') || (isClient && !cleaning.published)) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                            <span className="absolute inset-0 bg-buttonBg scale-x-0 group-hover:scale-x-100 origin-center transition-transform duration-300 ease-out z-0"/>
                            <Download className="w-4 h-4 relative z-10 transition-transform duration-300 ease-out group-hover:scale-90" />
                            <span className="relative z-10">Download Certificate</span>
                          </button>
                        )}
                        {actionButtons.edit && (
                          <button
                            onClick={() => navigate(`/tank-cleanings/edit/${cleaningId}`)}
                            disabled={isActionDisabled('edit')}
                            className={`relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-white text-buttonBg font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:text-white ${isActionDisabled('edit') ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                            <span className="absolute inset-0 bg-buttonBg scale-x-0 group-hover:scale-x-100 origin-center transition-transform duration-300 ease-out z-0"/>
                            <Pencil className="w-4 h-4 relative z-10 transition-transform duration-300 ease-out group-hover:scale-90" />
                            <span className="relative z-10">Edit Cleaning</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Details Sections */}
                  <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
                    {/* Basic Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Basic Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Cleaning ID</label>
                          <span className={`inline-block text-sm sm:text-base font-semibold px-0 py-1 rounded-lg ${
                            !isClient 
                              ? cleaning.published 
                                ? 'text-secondary' 
                                : 'text-secondary'
                              : 'text-secondary'
                          }`}>
                            {cleaning.cleaning_id}
                          </span>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Date of Survey</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(cleaning.date_of_survey)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Inspector</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{cleaning.inspected_by_name}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Previous Cargo</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{cleaning.previous_cargo || 'N/A'}</p>
                        </div>
                        {cleaning.location && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Location</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                              {cleaning.location.city}, {cleaning.location.state}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Tank Details */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Tank Details
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Tank No</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{cleaning.tank_no || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Tank Type</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{cleaning.tank_type || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Manufacture Date</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(cleaning.manufacture_date)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">IMO Class</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{cleaning.imo_class || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">UN Number</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{cleaning.un_number || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">MGW (kg)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{cleaning.maximum_gross_weight || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Tare Weight (kg)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{cleaning.tare_weight || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Capacity (Ltr)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{cleaning.capacity_ltr || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">CSC</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{cleaning.csc || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Cleaning Corporation</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{cleaning.cleaning_corporation || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Depot Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{cleaning.depot_name || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">LED</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(cleaning.led)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">NED</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(cleaning.ned)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Top Discharge</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                            {cleaning.top_discharge === 'yes' ? 'Yes' : 'No'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Syphon Tube</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                            {cleaning.syphon_tube === 'yes' ? 'Yes' : 'No'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Internal Description */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Internal Description
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Internal</label>
                          <p className="text-sm sm:text-base text-secondary whitespace-pre-wrap">
                            {cleaning.internal || 'The above equipment was inspected and found to be DRY, ODOURLESS and in a visually CLEAN condition. Valves and fittings were inspected at accessible points and found to be CLEAN'}
                          </p>
                        </div>
                        {cleaning.remarks && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Remarks</label>
                            <p className="text-sm sm:text-base text-secondary whitespace-pre-wrap">{cleaning.remarks}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Exterior Checklist */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Exterior Checklist
                      </h3>
                      <div className="space-y-3">
                        {cleaning.exterior && Object.entries({
                          frame_tank_walkways_free: 'Frame, tank and walkways free of contamination and cargo',
                          manlid_valve_compartments_free: 'Manlid and valve compartments free of contamination and cargo',
                          serial_numbers_legible: 'Serial numbers and statutory markings legible',
                          cargo_labels_removed: 'Cargo labels removed'
                        }).map(([key, label]) => (
                          <div key={key} className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-0.5">
                              {renderCheckbox(cleaning.exterior[key])}
                            </div>
                            <span className="text-sm sm:text-base text-secondary">{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Interior Checklist */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Interior Checklist
                      </h3>
                      <div className="space-y-3">
                        {cleaning.interior && Object.entries({
                          entry_made_by_surveyor: 'Entry made into tank by surveyor',
                          free_from_odour: 'Free from odour',
                          clean_free_from_cargo: 'Clean and free from all cargo and contamination',
                          free_from_corrosion: 'Free from corrosion or pitting',
                          dry: 'Dry'
                        }).map(([key, label]) => (
                          <div key={key} className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-0.5">
                              {renderCheckbox(cleaning.interior[key])}
                            </div>
                            <span className="text-sm sm:text-base text-secondary">{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Valves/Fittings Checklist */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Valves/Fittings (Free from contamination)
                      </h3>
                      <div className="space-y-3">
                        {cleaning.valves_fittings && Object.entries({
                          valves: 'Valves',
                          manlid_seal: 'Manlid Seal',
                          dip_pipe: 'Dip-Pipe',
                          spillage: 'Spillage',
                          gas_free_entry_permit: 'Gas Free Entry Permit Issued'
                        }).map(([key, label]) => (
                          <div key={key} className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-0.5">
                              {renderCheckbox(cleaning.valves_fittings[key])}
                            </div>
                            <span className="text-sm sm:text-base text-secondary">{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Additional Remarks */}
                    {cleaning.remarks_2 && (
                      <div>
                        <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                          Additional Remarks
                        </h3>
                        <p className="text-sm sm:text-base text-secondary whitespace-pre-wrap">{cleaning.remarks_2}</p>
                      </div>
                    )}

                    {/* System Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created By</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{cleaning.created_by || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created At</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(cleaning.created_at)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Last Updated</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(cleaning.updated_at)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Published Status</label>
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                            cleaning.published
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {cleaning.published ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                            {cleaning.published ? 'Published' : 'Draft'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* File Cabinet Modal */}
      {showFileCabinet && cleaning && (
        <TankCleaningFileCabinet 
          tankCleaningId={cleaning.cleaning_id} 
          clientId={cleaning.client_id} 
          onClose={() => setShowFileCabinet(false)} 
        />
      )}
    </div>
  );
};

export default ViewTankCleaning;
